import type { AdminSelf } from '@saay/api-client';
import {
  CardFaceRows,
  CardFaceUnit,
  IconTile,
  KpiCard,
  ListRow,
  Money,
  SignatureCard,
  StatusBadge,
} from '@saay/blocks';
import {
  formatCount,
  formatRatePercent,
  formatTimestamp,
  formatToman,
  formatTomanCompact,
} from '@saay/formatting';
import { Button, buttonVariants } from '@saay/shadcn/components/button';
import { Progress } from '@saay/shadcn/components/progress';
import { cn } from '@saay/shadcn/lib/utils';
import { DataState, EmptyState, PageHeader, Skeleton, StatusChip } from '../kit.js';
import { useLogin } from '@saay/web-app';
import {
  ArrowUpLeftIcon,
  ChevronLeftIcon,
  ClockAlertIcon,
  CoinsIcon,
  GaugeIcon,
  InboxIcon,
  RefreshCwIcon,
  SparklesIcon,
  StoreIcon,
} from 'lucide-react';
import { type ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';

import type { AdminDashboard, AdminMerchantSummary } from '../api.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { ADMIN_TOPBAR_EXTRA_ID, useShellSubtitle } from '../components/admin-shell.js';
import { RequireArea, usePageTitle } from '../components/guard.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { DARK_PILL, OUTLINE_PILL, SurfaceSection } from '../components/surface-section.js';
import { navigableRoutes } from '../nav.js';
import { canSeeArea, canUse, holdsAreaRole } from '../permissions.js';
import { listStatus, useAdminDashboard, useMerchants } from '../queries.js';

/**
 * `/dashboard` (FTD §9.1, AC §18.19.6 S4) in the D-93 admin sample: work waiting for the operator
 * comes first (ADM-03), then the platform KPIs. The next settlement joins that row (four columns
 * when it is present, three otherwise) so the row fills the width. Then active merchants
 * with the credit programs and grace period. Near-limit merchants use the dashboard list when
 * present, otherwise active merchants flagged `nearLimit` from `listAdminMerchants` (section 8
 * fallback).
 */

function compact(rials: number): string {
  return formatTomanCompact(rials);
}

/** A server rate ("0.580000") as a progress value; the rate itself is never recomputed. */
function ratePercent(rate: string | null | undefined): number {
  const value = Number(rate ?? '0');
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value * 100)) : 0;
}

/** The sample's capacity cell: a muted track on the white row, with the rate beside it. */
function CapacityMeter({
  label,
  nearLimit,
  rate,
}: {
  readonly label: string;
  readonly nearLimit: boolean;
  readonly rate: string | null | undefined;
}) {
  return (
    <span className="flex min-w-40 items-center gap-3">
      <Progress
        aria-label={label}
        className={cn(
          'min-w-16 flex-1 [&_[data-slot=progress-track]]:h-2 [&_[data-slot=progress-track]]:bg-muted-foreground/35',
          nearLimit && '[&_[data-slot=progress-indicator]]:bg-tone-copper-strong',
        )}
        value={ratePercent(rate)}
      />
      <span className="shrink-0 text-xs font-bold tabular-nums">
        {rate === null || rate === undefined ? '۰٪' : formatRatePercent(rate, { invalid: 'raw' })}
      </span>
    </span>
  );
}

/** The list a KPI opens (FTD §9.1: totals link to filtered lists). */
interface KpiLink {
  readonly label: string;
  readonly to: string;
}

/**
 * A KPI's list, only when that screen has shipped and the caller may open it (unshipped screens
 * stay out of reach, D-76): sales, payments, and settlements get their arrow when they ship.
 */
function kpiLink(self: AdminSelf, path: string, label: string, search = ''): KpiLink | undefined {
  return navigableRoutes(self).some((route) => route.path === path)
    ? { label, to: `${path}${search}` }
    : undefined;
}

/** The KPI value with its list link at the end, pointing up and forward in RTL. */
function KpiValue({ children, link }: { readonly children: ReactNode; readonly link?: KpiLink }) {
  return (
    <div className="flex items-end justify-between gap-2">
      {children}
      {link === undefined ? null : (
        <Link
          aria-label={link.label}
          className={buttonVariants({
            className: 'rounded-full bg-background',
            size: 'icon-sm',
            variant: 'ghost',
          })}
          title={link.label}
          to={link.to}
        >
          <ArrowUpLeftIcon aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

function KpiCards({
  dashboard,
  self,
}: {
  readonly dashboard: AdminDashboard;
  readonly self: AdminSelf;
}) {
  const { exposure, merchantCapacity } = dashboard;
  const sales = kpiLink(self, '/sales', 'فهرست فروش‌ها و قراردادها');
  const merchants = kpiLink(self, '/merchants', 'فهرست فروشگاه‌های فعال', '?status=ACTIVE');
  const payments = kpiLink(self, '/payments', 'فهرست اقساط و وصول');
  const showOverdue = dashboard.installments !== undefined;
  const showSettlement = dashboard.settlement !== undefined;
  const count = 2 + (showOverdue ? 1 : 0) + (showSettlement ? 1 : 0);
  return (
    <section
      aria-label="شاخص‌های پلتفرم"
      className={cn(
        'grid w-full grid-cols-1 gap-4 sm:grid-cols-2',
        count >= 4 ? 'xl:grid-cols-4' : count === 3 ? 'xl:grid-cols-3' : 'xl:grid-cols-2',
      )}
    >
      <KpiCard
        foot={`${formatCount(exposure.activeContractCount)} خرید فعال`}
        icon={CoinsIcon}
        label="اصل بدهی فعال پلتفرم"
        tone="olive"
        value={
          <KpiValue {...(sales === undefined ? {} : { link: sales })}>
            <Money rials={exposure.outstandingPrincipalIrr} size="lg" />
          </KpiValue>
        }
      />
      <KpiCard
        foot={
          <div className="flex w-full flex-col gap-2">
            <Progress
              aria-label="ظرفیت مصرف‌شدهٔ فروشگاه‌ها"
              className="w-full"
              value={ratePercent(merchantCapacity.utilizationRate)}
            />
            <span>{`از ${compact(merchantCapacity.approvedLimitIrr)} ظرفیت مصوب`}</span>
          </div>
        }
        icon={GaugeIcon}
        label="ظرفیت آزاد فروشگاه‌ها"
        tone="forest"
        value={
          <KpiValue {...(merchants === undefined ? {} : { link: merchants })}>
            <Money rials={merchantCapacity.availableCapacityIrr} size="lg" />
          </KpiValue>
        }
      />
      {dashboard.installments === undefined ? null : (
        <KpiCard
          foot="تعهد فروشگاه بدون تغییر"
          icon={ClockAlertIcon}
          label="اقساط در تأخیر"
          tone="danger"
          value={
            <KpiValue {...(payments === undefined ? {} : { link: payments })}>
              <Money rials={dashboard.installments.overdue.amountIrr} size="lg" />
            </KpiValue>
          }
        />
      )}
      {showSettlement ? <NextSettlement dashboard={dashboard} self={self} /> : null}
    </section>
  );
}

/** The next weekly settlement batch: the one figure on the card face (D-93). */
function NextSettlement({
  dashboard,
  self,
}: {
  readonly dashboard: AdminDashboard;
  readonly self: AdminSelf;
}) {
  const settlement = dashboard.settlement;
  if (settlement === undefined) {
    return null;
  }
  const batch = settlement.nextBatch;
  const link = kpiLink(self, '/settlements', 'فهرست تسویه‌ها');
  const text = batch === null ? '' : formatToman(batch.netIrr);
  const figure = text.slice(0, text.lastIndexOf(' ') === -1 ? undefined : text.lastIndexOf(' '));
  return (
    <section aria-label="تسویهٔ بعدی">
      <SignatureCard
        className="h-full"
        label="تسویهٔ بعدی"
        value={
          batch === null ? (
            <span className="text-xl">برنامه‌ریزی نشده</span>
          ) : (
            <>
              {figure}
              <CardFaceUnit />
            </>
          )
        }
      >
        {batch === null ? null : (
          <CardFaceRows
            items={[{ label: 'فروشگاه‌ها', value: formatCount(batch.merchantCount) }]}
          />
        )}
        {link === undefined ? null : (
          <Link
            className={buttonVariants({
              className: 'mt-4 w-full rounded-full bg-brand-gold text-brand hover:bg-brand-gold/90',
            })}
            to={link.to}
          >
            {link.label}
          </Link>
        )}
      </SignatureCard>
    </section>
  );
}

function WorkQueue({
  className,
  dashboard,
  nearLimitMerchants,
  pendingActivation,
  self,
}: {
  readonly className?: string;
  readonly dashboard: AdminDashboard;
  readonly nearLimitMerchants: readonly AdminMerchantSummary[];
  readonly pendingActivation: readonly AdminMerchantSummary[];
  readonly self: AdminSelf;
}) {
  const { approvals, confirmations } = dashboard;
  const rows: ReactNode[] = [];

  if (approvals !== undefined && canUse(self, 'listApprovalRequests')) {
    if (canUse(self, 'approveApprovalRequest') && approvals.decidableCount > 0) {
      rows.push(
        <ListRow
          end={
            <Link className={cn(buttonVariants(), DARK_PILL)} to="/approvals?view=decidable">
              بررسی
            </Link>
          }
          icon={InboxIcon}
          key="decidable"
          role="listitem"
          title={`${formatCount(approvals.decidableCount)} درخواست منتظر تصمیم شما`}
          tone="clay"
        />,
      );
    }
  }

  // Activation is work only for the roles that can activate (an Auditor saw «فعال‌سازی»).
  const activates = canUse(self, 'transitionMerchantStatus');
  for (const merchant of activates ? pendingActivation.slice(0, 5) : []) {
    rows.push(
      <ListRow
        description={`${merchant.city.nameFa} · ${merchant.primaryCategory.pathFa}`}
        end={
          <Link
            className={cn(buttonVariants({ variant: 'outline' }), OUTLINE_PILL)}
            state={OPENED_FROM_LIST}
            to={`/merchants/${merchant.id}`}
          >
            فعال‌سازی
          </Link>
        }
        icon={StoreIcon}
        key={merchant.id}
        role="listitem"
        title={merchant.displayNameFa}
        tone="clay"
      />,
    );
  }

  const nearLimitFromDashboard = dashboard.merchantCapacity.nearLimit.merchants;
  const nearLimit =
    nearLimitFromDashboard.length > 0
      ? nearLimitFromDashboard.map((entry) => ({
          id: entry.merchantId,
          name: entry.displayNameFa,
          detail: `${formatRatePercent(entry.utilizationRate, { invalid: 'raw' })} استفاده‌شده`,
        }))
      : nearLimitMerchants.map((entry) => ({
          id: entry.id,
          name: entry.displayNameFa,
          detail: `${formatRatePercent(entry.capacityUsage.utilizationRate ?? '0', { invalid: 'raw' })} استفاده‌شده`,
        }));

  for (const merchant of nearLimit.slice(0, 5)) {
    rows.push(
      <ListRow
        description={merchant.detail}
        end={
          <Link
            className={cn(buttonVariants({ variant: 'outline' }), OUTLINE_PILL)}
            state={OPENED_FROM_LIST}
            to={`/merchants/${merchant.id}`}
          >
            مشاهده
          </Link>
        }
        icon={GaugeIcon}
        key={merchant.id}
        role="listitem"
        title={`${merchant.name} نزدیک سقف ظرفیت`}
        tone="copper"
      />,
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <SurfaceSection
      {...(confirmations.expiringSoonCount > 0
        ? {
            action: (
              <StatusBadge tone="gold">
                <Link to="/buyers">{`${formatCount(confirmations.expiringSoonCount)} رو به انقضا`}</Link>
              </StatusBadge>
            ),
          }
        : {})}
      {...(className === undefined ? {} : { className })}
      id="dashboard-work"
      title="کارهای منتظر شما"
    >
      <div aria-label="کارهای منتظر شما" className="flex flex-col gap-2" role="list">
        {rows}
      </div>
    </SurfaceSection>
  );
}

/** The first page of active merchants (for reference after the work queue). */
function ActiveMerchants({ className }: { readonly className?: string }) {
  const merchants = useMerchants({ status: 'ACTIVE' });
  const navigate = useNavigate();
  const login = useLogin();
  const rows: readonly AdminMerchantSummary[] = (merchants.data?.pages[0]?.items ?? []).slice(0, 5);
  return (
    <SurfaceSection
      action={
        <Button
          className="text-primary"
          nativeButton={false}
          render={<Link to="/merchants?status=ACTIVE" />}
          size="sm"
          variant="ghost"
        >
          مدیریت فروشگاه‌ها
          <ChevronLeftIcon aria-hidden="true" />
        </Button>
      }
      {...(className === undefined ? {} : { className })}
      id="dashboard-merchants"
      title="فروشگاه‌های فعال"
    >
      <DataState
        empty={<EmptyState icon="store" title="هنوز فروشگاه فعالی وجود ندارد" />}
        error={merchants.error}
        isEmpty={rows.length === 0}
        onLogin={() => login()}
        onRetry={() => void merchants.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری فروشگاه‌ها…" lines={4} />}
        status={listStatus(merchants)}
      >
        <AdminListTable
          caption="فروشگاه‌های فعال"
          regionLabel="جدول فروشگاه‌های فعال"
          columns={[
            {
              header: 'فروشگاه',
              id: 'name',
              pin: 'start',
              render: (row) => (
                <span className="flex items-center gap-3">
                  <IconTile icon={StoreIcon} size="sm" tone="olive" />
                  <span className="saay-cell-main">
                    <Link
                      className="font-bold"
                      state={OPENED_FROM_LIST}
                      to={`/merchants/${row.id}`}
                    >
                      {row.displayNameFa}
                    </Link>
                    <small className="text-xs">{`${formatCount(row.activeBranchCount)} شعبه · ${row.city.nameFa}`}</small>
                  </span>
                </span>
              ),
            },
            {
              header: 'ظرفیت مصوب',
              id: 'limit',
              render: (row) => <Money rials={row.capacityUsage.approvedLimitIrr} size="sm" />,
            },
            {
              header: 'ظرفیت مصرف‌شده',
              id: 'used',
              render: (row) => (
                <CapacityMeter
                  label={`ظرفیت مصرف‌شدهٔ ${row.displayNameFa}`}
                  nearLimit={row.capacityUsage.nearLimit}
                  rate={row.capacityUsage.utilizationRate}
                />
              ),
            },
            {
              header: 'نرخ SAAY',
              id: 'rate',
              render: (row) =>
                row.saayFeeRate === null
                  ? '—'
                  : formatRatePercent(row.saayFeeRate, { invalid: 'raw' }),
            },
            {
              header: 'وضعیت',
              id: 'status',
              pin: 'end',
              render: (row) =>
                row.capacityUsage.nearLimit ? (
                  <StatusBadge tone="copper">نزدیک سقف</StatusBadge>
                ) : (
                  <StatusChip enumName="merchantStatus" value={row.status} />
                ),
            },
          ]}
          mobileSummary={(row) => ({
            primary: row.displayNameFa,
            secondary: `ظرفیت ${compact(row.capacityUsage.usedCapacityIrr)} از ${compact(row.capacityUsage.approvedLimitIrr)}`,
          })}
          onRowClick={(row) => void navigate(`/merchants/${row.id}`, { state: OPENED_FROM_LIST })}
          rowKey={(row) => row.id}
          rows={rows}
        />
      </DataState>
    </SurfaceSection>
  );
}

/** «به‌روز …» lives in the bar subtitle; «تازه‌سازی» sits with the bar controls, not on its own row. */
function DashboardTopBar({
  asOf,
  onRefresh,
}: {
  readonly asOf: string | undefined;
  readonly onRefresh: () => void;
}) {
  useShellSubtitle(
    asOf === undefined ? null : `به‌روز ${formatTimestamp(asOf, { withTime: true })}`,
    asOf !== undefined,
  );
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setSlot(document.getElementById(ADMIN_TOPBAR_EXTRA_ID));
  }, []);
  if (asOf === undefined || slot === null) {
    return null;
  }
  return createPortal(
    <Button
      className="h-10 shrink-0 rounded-full bg-muted px-4"
      onClick={onRefresh}
      type="button"
      variant="ghost"
    >
      <RefreshCwIcon aria-hidden="true" />
      تازه‌سازی
    </Button>,
    slot,
  );
}

function DashboardView({ self }: { readonly self: AdminSelf }) {
  const dashboard = useAdminDashboard();
  const login = useLogin();
  const pendingMerchants = useMerchants({ status: 'PENDING_ACTIVATION' });
  const activeMerchants = useMerchants({ status: 'ACTIVE' });
  const pendingRows = (pendingMerchants.data?.pages[0]?.items ?? []).slice(0, 5);
  const nearLimitFallback = (activeMerchants.data?.pages[0]?.items ?? [])
    .filter((row) => row.capacityUsage.nearLimit)
    .slice(0, 5);

  const readsPrograms = canSeeArea(self, 'creditPrograms');
  const readsPolicies = holdsAreaRole(self, 'policies');
  const { installments, graceDays, creditPrograms } = dashboard.data ?? {};
  const showPrograms =
    readsPrograms && creditPrograms !== undefined && creditPrograms.activeProgramCount > 0;
  const showGrace =
    readsPolicies && installments !== undefined && installments.gracePeriod.count > 0;
  return (
    <>
      <DashboardTopBar asOf={dashboard.data?.asOf} onRefresh={() => void dashboard.refetch()} />
      <PageHeader title="داشبورد پلتفرم" />
      <DataState
        error={dashboard.error}
        onLogin={() => login()}
        onRetry={() => void dashboard.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری شاخص‌ها…" lines={6} />}
        status={dashboard.status}
      >
        {dashboard.data === undefined ? null : (
          <>
            <WorkQueue
              dashboard={dashboard.data}
              nearLimitMerchants={nearLimitFallback}
              pendingActivation={pendingRows}
              self={self}
            />
            <KpiCards dashboard={dashboard.data} self={self} />
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <ActiveMerchants
                className={
                  showPrograms || showGrace ? 'min-w-0 xl:col-span-2' : 'min-w-0 xl:col-span-3'
                }
              />
              {showPrograms || showGrace ? (
                <div className="flex flex-col gap-4">
                  {showPrograms ? (
                    <SurfaceSection
                      action={
                        <StatusBadge tone="gold">
                          {`${formatCount(creditPrograms.activeProgramCount)} فعال`}
                        </StatusBadge>
                      }
                      id="dashboard-programs"
                      title={
                        creditPrograms.soonestEndingProgram === null
                          ? 'برنامه‌های اعتباری'
                          : `${creditPrograms.soonestEndingProgram.nameFa} تا ${formatCount(creditPrograms.soonestEndingProgram.daysRemaining)} روز دیگر`
                      }
                    >
                      <ListRow
                        end={
                          <ChevronLeftIcon
                            aria-hidden="true"
                            className="size-4 text-muted-foreground"
                          />
                        }
                        icon={SparklesIcon}
                        render={
                          <Link
                            to={
                              creditPrograms.soonestEndingProgram === null
                                ? '/credit-programs'
                                : `/credit-programs/${creditPrograms.soonestEndingProgram.programId}`
                            }
                          />
                        }
                        title="مشاهدهٔ برنامه‌ها"
                        tone="gold"
                      />
                    </SurfaceSection>
                  ) : null}
                  {showGrace ? (
                    <SurfaceSection
                      {...(graceDays === undefined || graceDays === null
                        ? {}
                        : {
                            action: (
                              <StatusBadge tone="clay">{`${formatCount(graceDays)} روز مهلت`}</StatusBadge>
                            ),
                          })}
                      id="dashboard-collections"
                      title={`${formatCount(installments.gracePeriod.count)} قسط در دورهٔ دیرکرد`}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        )}
      </DataState>
    </>
  );
}

export function DashboardPage() {
  usePageTitle('داشبورد', true, 'داشبورد پلتفرم');
  return <RequireArea area="dashboard">{(self) => <DashboardView self={self} />}</RequireArea>;
}
